// Enlaza la validación al DOM del formulario público (ERB server-rendered).
// Lee la metadata de campos embebida, valida requeridos y formato por tipo en
// cada blur/input, y bloquea el envío mostrando el error inline bajo el campo.
// Se usa `novalidate` para mostrar mensajes propios consistentes en vez de los
// del navegador. El servidor revalida siempre (validación autoritativa).

import { validateValue } from './formValidation';

const FORM_ID = 'portal-form';
const META_NODE_ID = 'form-fields-meta';
const TYPE_ERROR_CLASSES = [
  'validation-message',
  'mt-1',
  'text-xs',
  'font-medium',
  'text-red-600',
  'dark:text-red-400',
];
const INVALID_BORDER_CLASS = 'field-invalid';

const readFieldsMeta = () => {
  const node = document.getElementById(META_NODE_ID);
  if (!node) return [];
  try {
    const parsed = JSON.parse(node.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

// Un campo se ignora si fue ocultado por reglas (wrapper sin mostrar o control deshabilitado).
const isFieldActive = name => {
  const wrapper = document.getElementById(`field-${name}`);
  if (!wrapper || wrapper.style.display === 'none') return false;
  const control = wrapper.querySelector('input, select, textarea');
  return !control || !control.disabled;
};

const controlFor = name => {
  const wrapper = document.getElementById(`field-${name}`);
  return wrapper ? wrapper.querySelector('input, select, textarea') : null;
};

const collectValues = form => {
  const values = {};
  form.querySelectorAll('[name]').forEach(element => {
    if (!element.name) return;
    if (element.type === 'checkbox') {
      values[element.name] = element.checked ? 'true' : 'false';
    } else {
      values[element.name] = element.value;
    }
  });
  return values;
};

const isEmpty = value => !value || !String(value).trim();

const isRequiredMissing = (field, value) => {
  if (!field.required) return false;
  if (field.type === 'checkbox') return value !== 'true';
  return isEmpty(value);
};

const typeErrorNode = name => {
  const wrapper = document.getElementById(`field-${name}`);
  if (!wrapper) return null;

  const id = `validation-message-${name}`;
  let node = document.getElementById(id);
  if (!node) {
    node = document.createElement('p');
    node.id = id;
    node.classList.add(...TYPE_ERROR_CLASSES);
    wrapper.appendChild(node);
  }
  return node;
};

const toggleInvalidBorder = (name, invalid) => {
  const control = controlFor(name);
  if (control) control.classList.toggle(INVALID_BORDER_CLASS, invalid);
};

const toggleRequiredMessage = (name, show) => {
  const node = document.getElementById(`required-message-${name}`);
  if (node) node.classList.toggle('hidden', !show);
};

const setTypeError = (name, message) => {
  const node = typeErrorNode(name);
  if (node) node.textContent = message || '';
};

// Refleja el estado de validación de un campo: requerido vacío, error de tipo o válido.
const renderFieldState = (field, value) => {
  const requiredMissing = isRequiredMissing(field, value);
  const typeMessage = requiredMissing ? '' : validateValue(field, value);

  toggleRequiredMessage(field.name, requiredMissing);
  setTypeError(field.name, typeMessage);
  toggleInvalidBorder(field.name, requiredMissing || !!typeMessage);

  return requiredMissing || !!typeMessage;
};

const clearFieldState = name => {
  toggleRequiredMessage(name, false);
  setTypeError(name, '');
  toggleInvalidBorder(name, false);
};

const activeFields = fields =>
  fields.filter(field => isFieldActive(field.name));

const computeFormValidity = (form, fields) => {
  const values = collectValues(form);
  return activeFields(fields).every(field => {
    const value = values[field.name];
    if (isRequiredMissing(field, value)) return false;
    if (validateValue(field, value)) return false;
    return true;
  });
};

const syncButtonState = (form, fields) => {
  const btn = document.getElementById('submit-button');
  if (!btn) return;
  if (btn.classList.contains('loading') || btn.classList.contains('submitted'))
    return;
  // El captcha está activado pero mal configurado: nunca se puede emitir un
  // token válido, así que el envío se mantiene bloqueado pase lo que pase.
  if (btn.dataset.captchaMisconfigured === 'true') {
    btn.disabled = true;
    return;
  }
  btn.disabled = !computeFormValidity(form, fields);
};

const blockInvalidSubmit = (form, fields, event) => {
  const values = collectValues(form);
  const invalid = activeFields(fields).filter(field =>
    renderFieldState(field, values[field.name])
  );
  if (!invalid.length) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const firstWrapper = document.getElementById(`field-${invalid[0].name}`);
  if (firstWrapper) {
    firstWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

const onFieldEvent = (form, fields, event) => {
  const control = event.target;
  if (!control || !control.name) return;

  const field = fields.find(item => item.name === control.name);
  if (!field || !isFieldActive(field.name)) return;

  const values = collectValues(control.form || document);
  renderFieldState(field, values[field.name]);
  syncButtonState(form, fields);
};

export const initFormValidation = () => {
  const form = document.getElementById(FORM_ID);
  if (!form || form.dataset.validationInitialized === 'true') return;

  const fields = readFieldsMeta();
  if (!fields.length) return;

  form.dataset.validationInitialized = 'true';
  // Desactiva los mensajes nativos del navegador para usar los propios.
  form.setAttribute('novalidate', 'novalidate');

  // Expone sincronización del botón para que el código inline pueda re-evaluar tras errores de servidor.
  window.syncFormButtonState = () => syncButtonState(form, fields);

  // Captura en document para correr antes del listener de envío inline de la vista.
  document.addEventListener(
    'submit',
    event => {
      if (event.target && event.target.id === FORM_ID) {
        blockInvalidSubmit(form, fields, event);
      }
    },
    true
  );

  form.addEventListener(
    'blur',
    event => onFieldEvent(form, fields, event),
    true
  );
  form.addEventListener('change', event => onFieldEvent(form, fields, event));
  form.addEventListener('input', event => {
    const control = event.target;
    if (!control || !control.name) return;
    // Al escribir, limpiar el estado para no mostrar error mientras se corrige.
    clearFieldState(control.name);
    syncButtonState(form, fields);
  });

  // Estado inicial del botón según validez actual del formulario.
  syncButtonState(form, fields);
};
